/**
 * AI Drafts API Functions
 *
 * Routes mount at /api/ai-core/* (platform convention).
 */

import type { AiBundle, AiBundleStatus } from '@lognai/plugin-ai-core/src/types/bundle';
import type { AiDraftItem } from '@lognai/plugin-ai-core/src/types/draft-item';
import { apiFetch } from './client';

const BASE_URL = '/api/ai-core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ListBundlesParams {
  threadId?: string;
  status?: AiBundleStatus | AiBundleStatus[]; // EXPANDED — Applied/Rejected tabs filter by 2-3 statuses
  createdByUserId?: string; // NEW
  limit?: number;
  cursor?: string; // ISO date
}

export interface BundleWithItems {
  bundle: AiBundle;
  items: AiDraftItem[];
}

export interface ApplyBundleResultItem {
  draftId: string;
  status: 'applied' | 'failed' | 'skipped';
  error?: string;
  undoDeadlineAt?: string;
}

export interface ApplyBundleResult {
  items: ApplyBundleResultItem[];
}

// ---------------------------------------------------------------------------
// Read endpoints
// ---------------------------------------------------------------------------

export const listAiBundles = async (params: ListBundlesParams = {}): Promise<AiBundle[]> => {
  const qs = new URLSearchParams();
  if (params.threadId) qs.set('threadId', params.threadId);
  if (params.status !== undefined) {
    const value = Array.isArray(params.status) ? params.status.join(',') : params.status;
    qs.append('status', value);
  }
  if (params.createdByUserId) qs.append('createdByUserId', params.createdByUserId);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiFetch<AiBundle[]>(`${BASE_URL}/drafts/bundles${suffix}`, { credentials: 'include' });
};

export const getAiBundle = async (bundleId: string): Promise<BundleWithItems> => {
  return apiFetch<BundleWithItems>(
    `${BASE_URL}/drafts/bundles/${encodeURIComponent(bundleId)}`,
    { credentials: 'include' },
  );
};

export const listAiPendingApprovals = async (): Promise<AiDraftItem[]> => {
  return apiFetch<AiDraftItem[]>(`${BASE_URL}/drafts/pending-approvals`, { credentials: 'include' });
};

export const listAiDraftsByWorkflowRun = async (
  workflowRunId: string,
): Promise<AiDraftItem[]> => {
  return apiFetch<AiDraftItem[]>(
    `${BASE_URL}/drafts/workflow-runs/${encodeURIComponent(workflowRunId)}`,
    { credentials: 'include' },
  );
};

// ---------------------------------------------------------------------------
// Mutation endpoints
// ---------------------------------------------------------------------------

export const approveAiDraft = async (draftId: string): Promise<void> => {
  await apiFetch<void>(
    `${BASE_URL}/drafts/items/${encodeURIComponent(draftId)}/approve`,
    { method: 'POST', credentials: 'include' },
  );
};

export const rejectAiDraft = async (draftId: string, reason?: string): Promise<void> => {
  await apiFetch<void>(
    `${BASE_URL}/drafts/items/${encodeURIComponent(draftId)}/reject`,
    {
      method: 'POST',
      credentials: 'include',
      headers: reason ? { 'Content-Type': 'application/json' } : undefined,
      body: reason ? JSON.stringify({ reason }) : undefined,
    },
  );
};

export const applyAiDraftItem = async (draftId: string): Promise<ApplyBundleResultItem> => {
  return apiFetch<ApplyBundleResultItem>(
    `${BASE_URL}/drafts/items/${encodeURIComponent(draftId)}/apply`,
    { method: 'POST', credentials: 'include' },
  );
};

export const applyAiBundle = async (bundleId: string): Promise<ApplyBundleResult> => {
  return apiFetch<ApplyBundleResult>(
    `${BASE_URL}/drafts/bundles/${encodeURIComponent(bundleId)}/apply`,
    { method: 'POST', credentials: 'include' },
  );
};

export const cancelAiBundle = async (bundleId: string, reason?: string): Promise<void> => {
  await apiFetch<void>(
    `${BASE_URL}/drafts/bundles/${encodeURIComponent(bundleId)}/cancel`,
    {
      method: 'POST',
      credentials: 'include',
      headers: reason ? { 'Content-Type': 'application/json' } : undefined,
      body: reason ? JSON.stringify({ reason }) : undefined,
    },
  );
};

export const undoAiDraftItem = async (draftId: string): Promise<void> => {
  await apiFetch<void>(
    `${BASE_URL}/drafts/items/${encodeURIComponent(draftId)}/undo`,
    { method: 'POST', credentials: 'include' },
  );
};
